import { useMemo, useRef, useState, type ReactNode } from "react"
import {
  portalRequestAnchor,
  type PortalRequestResponse,
  type PortalSessionResponse,
} from "@ledgerbox/domain"

import { errorMessage } from "./api"
import { usePortalApi } from "./api-context"
import { ChecklistItem, UploadQueue } from "./checklist"
import {
  countInWords,
  formatShortDate,
  plural,
  requestLabel,
  requestLine,
} from "./format"
import { homeCopy } from "./home-copy"
import { AccountingAction, ExternalHandoff } from "./item-actions"
import { ItemExample } from "./item-example"
import {
  everythingAccepted,
  heldFileLabel,
  isDoneTask,
  isSentBack,
  isTransactionQuestion,
  taskSections,
  requiredTasksLeft,
  taskStateLabel,
  taskStateTone,
  withShownItems,
  canUndoClientDone,
} from "./item-state"
import { useLandOnItem, type ItemToFix } from "./items-to-fix"
import { ItemThread, useItemThreads } from "./portal-threads"
import { LazySigningScreen } from "./signing/lazy-signing-screen"
import { useDraftMemory } from "./use-item-draft"
import { HiddenFileInput, InlineError } from "./ui"
import { itemQueueUploads, type UploadItem } from "./upload"

type Task = {
  request: PortalRequestResponse
  item: PortalRequestResponse["items"][number]
}
type Target = { requestId: string; itemId: string }

export function EngagementWorkbench({
  session,
  firmName,
  uploads,
  queueNote,
  actionError,
  onFiles,
  onRetry,
  onDismissUpload,
  onSessionChange,
  onOpenMessages,
  onOpenDeliverables,
  onOpenHistory,
  itemToFix,
  onLanded,
}: {
  session: PortalSessionResponse
  firmName: string
  uploads: UploadItem[]
  queueNote: string | null
  actionError: string | null
  onActionError(next: string | null): void
  onFiles(files: FileList | File[], target?: Target): Promise<void>
  onRetry(item: UploadItem): Promise<void>
  onDismissUpload(id: string): void
  onSessionChange(session: PortalSessionResponse): void
  onOpenMessages(): void
  onOpenDeliverables(): void
  /** "Everything you have sent", the History section. */
  onOpenHistory(): void
  /** The sent-back item the header button named: it opens here. */
  itemToFix?: ItemToFix | null
  onLanded?(): void
}) {
  const api = usePortalApi()
  const requests = useMemo(
    () => session.requests.map(withShownItems),
    [session.requests]
  )
  const tasks = useMemo(
    () =>
      requests.flatMap((request) =>
        request.items.map((item) => ({ request, item }))
      ),
    [requests]
  )
  const [expanded, setExpanded] = useState<string | null>(
    itemToFix?.itemId ?? null
  )
  // A press of the header button opens its item in this same render, so the
  // item is drawn open by the time useLandOnItem moves focus into it.
  const [openedFor, setOpenedFor] = useState(itemToFix ?? null)
  if (itemToFix && itemToFix !== openedFor) {
    setOpenedFor(itemToFix)
    setExpanded(itemToFix.itemId)
  }
  useLandOnItem(itemToFix, (itemId) => `engagement-task-${itemId}`, onLanded)
  const [signingId, setSigningId] = useState<string | null>(null)
  // Done rows fold away under one line per section, so the open work and
  // the one "I've sent everything" stay near the top (R5-U11).
  const [showDone, setShowDone] = useState<Record<string, boolean>>({})
  const threads = useItemThreads()
  const looseInput = useRef<HTMLInputElement>(null)
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const refresh = async () => onSessionChange(await api.getSession())
  const rememberDraft = useDraftMemory(session, onSessionChange)
  /* "I've sent everything", or its undo. Both kinds of request undo the
     same way, until the firm closes the request. */
  const submit = async (request: PortalRequestResponse, undo = false) => {
    setPendingRequestId(request.requestId)
    setSubmitError(null)
    try {
      if (undo) await api.undoRequestDone(request.requestId)
      else if (request.engagementId)
        await api.submitEngagementRequest(request.requestId)
      else await api.markRequestDone(request.requestId)
      await refresh()
    } catch (error) {
      setSubmitError(
        errorMessage(
          error instanceof Error
            ? error
            : new Error("Something went wrong. Try again.")
        )
      )
    } finally {
      setPendingRequestId(null)
    }
  }

  const multipleRequests = session.requests.length > 1
  const renderTask = ({ request, item }: Task) => {
    const active = expanded === item.itemId
    const sentBack = isSentBack(item)
    const files = session.files.filter(
      (file) => file.requestItemId === item.itemId
    )
    const itemUploads = active
      ? itemQueueUploads(uploads, item.itemId, files)
      : []
    return (
      <article
        className={`engagement-task${sentBack ? " is-sent-back" : ""}`}
        key={item.itemId}
      >
        <button
          id={`engagement-task-toggle-${item.itemId}`}
          className="engagement-task-toggle"
          type="button"
          aria-expanded={active}
          aria-controls={`engagement-task-${item.itemId}`}
          onClick={() => setExpanded(active ? null : item.itemId)}
        >
          <span className="engagement-task-copy">
            <strong>{item.name}</strong>
            {/* With one request open its name heads the page, so the rows
                do not repeat it. A question names its own statement and
                month, and sits under its own heading. */}
            {multipleRequests && !isTransactionQuestion(item) ? (
              <small>{requestLine(request)}</small>
            ) : null}
            {/* The firm's reason stays readable with the row closed. */}
            {sentBack ? (
              <small className="engagement-task-reason">
                {item.rejectionReason}
              </small>
            ) : null}
          </span>
          <span
            className="engagement-task-state"
            data-tone={taskStateTone(item)}
          >
            {taskStateLabel(item)}
          </span>
        </button>
        {active ? (
          <div
            id={`engagement-task-${item.itemId}`}
            className="engagement-task-detail"
            role="group"
            aria-labelledby={`engagement-task-toggle-${item.itemId}`}
            // The items-to-fix button moves focus here.
            tabIndex={-1}
          >
            {/* A plain item shows its example inside its own answer. */}
            {item.kind === "accounting_access" ||
            item.kind === "external_handoff" ? (
              <ItemExample item={item} />
            ) : null}
            {item.kind === "accounting_access" ? (
              <AccountingAction
                request={request}
                item={item}
                files={files}
                firmName={firmName}
                uploadAllowed={session.uploadAllowed}
                onFiles={onFiles}
                onRefresh={refresh}
              />
            ) : item.kind === "external_handoff" ? (
              <ExternalHandoff
                request={request}
                item={item}
                onRefresh={refresh}
              />
            ) : (
              <ChecklistItem
                item={item}
                files={files}
                uploadAllowed={session.uploadAllowed && !request.clientDoneAt}
                firmName={firmName}
                categoryChoices={request.categoryChoices}
                contactId={session.contact.contactId}
                onSign={setSigningId}
                onFiles={(selected) =>
                  void onFiles(selected, {
                    requestId: request.requestId,
                    itemId: item.itemId,
                  })
                }
                onAnswered={() => void refresh()}
                onDraftSaved={(draft) => rememberDraft(item.itemId, draft)}
                // The list below names each file with its state.
                filePills={false}
                // The task row above names the item and shows its state.
                heading={false}
              />
            )}
            {files.length ? (
              <ul
                className="engagement-file-list"
                aria-label={`Files for ${item.name}`}
              >
                {files.map((file) => (
                  <li key={file.fileId}>
                    <span>{file.fileName}</span>
                    <span>{heldFileLabel(file, item)}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {/* A file sent to this item reports here, where it was sent,
                not at the foot of the page. */}
            {itemUploads.length ? (
              <UploadQueue
                uploads={itemUploads}
                onRetry={(upload) => void onRetry(upload)}
                onDismiss={onDismissUpload}
                dismissSent
              />
            ) : null}
            {item.blockerReason ? (
              <p className="engagement-task-note">{item.blockerReason}</p>
            ) : null}
            {/* Asking is one quiet line that opens the box, so an item's
                own answer and its Send lead, and a signed or accepted item
                does not end in an empty box (R3-U7). */}
            <ItemThread
              item={item}
              thread={threads.threadFor(item.itemId)}
              firmName={firmName}
              collapsed
              onPosted={() => void threads.refresh()}
            />
            {request.engagementId ? (
              <TaskHelp
                request={request}
                item={item}
                // Help, a waiver and a changed response all write on the
                // item's thread, so the thread is read again too.
                onRefresh={() =>
                  Promise.all([refresh(), threads.refresh()]).then(
                    () => undefined
                  )
                }
                onOpenMessages={onOpenMessages}
              />
            ) : null}
          </div>
        ) : null}
      </article>
    )
  }

  if (!requests.length) return null
  const own = homeCopy(session, firmName)
  const { needed, questions, later } = taskSections(tasks, ({ item }) => item)
  // Files sent to the open item show under it; the rest show here.
  const looseUploads = uploads.filter(
    (upload) => !upload.requestItemId || upload.requestItemId !== expanded
  )
  return (
    <div className="page engagement-workbench">
      <div className="page-heading section-gap">
        <h1>{own.heading ?? "Your tasks"}</h1>
        <p className="lede">
          {own.lede ??
            `Send the information ${firmName} needs. The firm will check it before work begins.`}
        </p>
      </div>
      <TaskSection
        id="needed-to-begin"
        title="Needed to begin"
        tasks={needed}
        empty="Nothing is needed from you now."
        render={renderTask}
        expanded={expanded}
        showDone={Boolean(showDone["needed-to-begin"])}
        onShowDone={(next) =>
          setShowDone((current) => ({ ...current, "needed-to-begin": next }))
        }
      />
      {questions.length ? (
        <TaskSection
          id="transaction-questions"
          title="Questions about your transactions"
          tasks={questions}
          empty="Nothing is left to answer."
          render={renderTask}
          expanded={expanded}
          showDone={Boolean(showDone["transaction-questions"])}
          onShowDone={(next) =>
            setShowDone((current) => ({
              ...current,
              "transaction-questions": next,
            }))
          }
        />
      ) : null}
      {later.length ? (
        <TaskSection
          id="can-wait"
          title="Can wait"
          tasks={later}
          empty="Nothing here waits on you."
          render={renderTask}
          expanded={expanded}
          showDone={Boolean(showDone["can-wait"])}
          onShowDone={(next) =>
            setShowDone((current) => ({ ...current, "can-wait": next }))
          }
        />
      ) : null}
      {requests.map((request) => {
        const remaining = requiredTasksLeft(
          request.items,
          Boolean(request.engagementId)
        )
        // The firm already has everything: the client is not asked to send
        // anything (R8-F2).
        const allIn = !request.clientDoneAt && everythingAccepted(request.items)
        // "I've sent everything" shows once it can be pressed. An off button on
        // every open request only filled the foot of the page (R5-U11).
        const ready = !request.clientDoneAt && !allIn && remaining.length === 0
        return (
          <section
            key={request.requestId}
            // The email's `#request-<id>` lands on this request's summary.
            id={portalRequestAnchor(request.requestId)}
            className="engagement-submit"
            aria-label={requestLabel(request)}
          >
            <div>
              <h2 className="engagement-submit-title">
                {requestLabel(request)}
              </h2>
              <p>
                {request.clientDoneAt
                  ? `You told ${firmName} you sent everything`
                  : allIn
                    ? `${firmName} has everything it needs`
                    : remaining.length
                      ? `Send ${countInWords(remaining.length)} more required ${plural(remaining.length, "item", "items")}`
                      : `This tells ${firmName} you have nothing more to add`}
              </p>
              {request.dueAt && !request.clientDoneAt && !allIn ? (
                <p className="engagement-submit-due">
                  Due {formatShortDate(request.dueAt)}
                </p>
              ) : null}
            </div>
            {ready ? (
              <button
                className="accent-pill"
                type="button"
                disabled={pendingRequestId === request.requestId}
                onClick={() => void submit(request)}
              >
                {pendingRequestId === request.requestId
                  ? "Telling them…"
                  : "I've sent everything"}
              </button>
            ) : canUndoClientDone(request) ? (
              <button
                className="ghost-pill"
                type="button"
                disabled={pendingRequestId === request.requestId}
                onClick={() => void submit(request, true)}
              >
                Undo
              </button>
            ) : null}
          </section>
        )
      })}
      <LazySigningScreen
        signatureId={signingId ?? ""}
        open={signingId !== null}
        onClose={() => setSigningId(null)}
        onSigned={() => void refresh()}
      />
      {submitError ? <InlineError message={submitError} /> : null}
      {actionError ? <InlineError message={actionError} /> : null}
      {queueNote ? <p className="view-note">{queueNote}</p> : null}
      {looseUploads.length ? (
        <UploadQueue
          uploads={looseUploads}
          onRetry={(item) => void onRetry(item)}
          onDismiss={onDismissUpload}
          dismissSent
        />
      ) : null}
      <div className="engagement-secondary-actions">
        <button type="button" className="text-button" onClick={onOpenMessages}>
          Messages
        </button>
        <button
          type="button"
          className="text-button"
          onClick={onOpenDeliverables}
        >
          Files from the firm
        </button>
        <button type="button" className="text-button" onClick={onOpenHistory}>
          Everything you have sent
        </button>
        <HiddenFileInput
          ref={looseInput}
          multiple
          onFiles={(selected) => void onFiles(selected)}
        />
        <button
          type="button"
          className="text-button"
          disabled={!session.uploadAllowed}
          onClick={() => looseInput.current?.click()}
        >
          Add another file
        </button>
      </div>
    </div>
  )
}

/* One section of the task list. Done rows fold under one line, so 23
   settled questions do not push the open work down the page (R5-U11). The
   row that is open stays drawn even when it is done. */
function TaskSection({
  id,
  title,
  tasks,
  empty,
  render,
  expanded,
  showDone,
  onShowDone,
}: {
  id: string
  title: string
  tasks: Task[]
  empty: string
  render(task: Task): ReactNode
  expanded: string | null
  showDone: boolean
  onShowDone(next: boolean): void
}) {
  const done = tasks.filter(({ item }) => isDoneTask(item))
  const shown = showDone
    ? tasks
    : tasks.filter(({ item }) => !isDoneTask(item) || item.itemId === expanded)
  const hiddenCount = tasks.length - shown.length
  return (
    <section aria-labelledby={id} className="panel engagement-task-panel">
      <div className="panel-head">
        <h2 id={id}>{title}</h2>
      </div>
      {shown.length ? shown.map(render) : <p className="panel-note">{empty}</p>}
      {done.length && (showDone || hiddenCount > 0) ? (
        <button
          type="button"
          className="text-button engagement-done-toggle"
          aria-expanded={showDone}
          onClick={() => onShowDone(!showDone)}
        >
          {showDone
            ? "Hide the done items"
            : `Show ${hiddenCount} done ${hiddenCount === 1 ? "item" : "items"}`}
        </button>
      ) : null}
    </section>
  )
}

function TaskHelp({
  request,
  item,
  onRefresh,
  onOpenMessages,
}: {
  request: PortalRequestResponse
  item: PortalRequestResponse["items"][number]
  onRefresh(): Promise<void>
  onOpenMessages(): void
}) {
  const api = usePortalApi()
  const [mode, setMode] = useState<"help" | "waiver" | "reopen" | null>(null)
  const [reason, setReason] = useState("")
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const save = async () => {
    if (!mode || !reason.trim()) return
    setPending(true)
    setError(null)
    try {
      const input = {
        expectedVersion: item.evidenceVersion ?? 1,
        reason: reason.trim(),
      }
      if (mode === "reopen")
        await api.reopenEngagementItem(request.requestId, item.itemId, input)
      else
        await api.requestEngagementHelp(request.requestId, item.itemId, {
          ...input,
          kind: mode,
        })
      setMode(null)
      setReason("")
      await onRefresh()
      if (mode === "help") onOpenMessages()
    } catch (cause) {
      setError(
        errorMessage(
          cause instanceof Error
            ? cause
            : new Error("Something went wrong. Try again.")
        )
      )
    } finally {
      setPending(false)
    }
  }
  return (
    <div className="engagement-help">
      <div className="engagement-help-actions">
        <button
          type="button"
          className="text-button"
          onClick={() => setMode("help")}
        >
          I need help
        </button>
        <button
          type="button"
          className="text-button"
          onClick={() => setMode("waiver")}
        >
          Does not apply
        </button>
        {/* A signature cannot be taken back, so a signed letter has no
            response to change. */}
        {item.kind !== "signature" &&
        (item.status === "accepted" || item.status === "received") ? (
          <button
            type="button"
            className="text-button"
            onClick={() => setMode("reopen")}
          >
            Change my response
          </button>
        ) : null}
      </div>
      {mode ? (
        <div className="engagement-help-form">
          <label htmlFor={`help-${item.itemId}`}>
            {mode === "waiver"
              ? "Why does this not apply?"
              : mode === "reopen"
                ? "What changed?"
                : "What do you need help with?"}
          </label>
          <textarea
            id={`help-${item.itemId}`}
            value={reason}
            maxLength={2000}
            rows={3}
            onChange={(event) => setReason(event.currentTarget.value)}
          />
          <div>
            <button
              type="button"
              className="ghost-pill"
              disabled={pending || !reason.trim()}
              onClick={() => void save()}
            >
              {pending ? "Sending…" : "Send to firm"}
            </button>
            <button
              type="button"
              className="text-button"
              onClick={() => setMode(null)}
            >
              Cancel
            </button>
          </div>
          {/* Only a request to skip waits on the firm's yes. */}
          {mode === "waiver" ? (
            <p className="panel-note">
              The firm decides whether you can skip this.
            </p>
          ) : null}
        </div>
      ) : null}
      {error ? <InlineError message={error} /> : null}
    </div>
  )
}
